import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from './ui/dialog';
import {
  AlertCircle,
  Calculator,
  Calendar,
  Loader2,
  PlusCircle,
  Send,
  ShieldCheck,
  X
} from 'lucide-react';
import { Button } from './ui/button';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import ToastPortal from './ToastPortal';
import { api } from '../services/api';

const formatMontant = (montant) => (
  `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`
);

// Les modes s'adaptent au contexte. Un élève seul n'a pas de valeur famille à
// reprendre : l'option n'existe pas pour lui. Un membre de famille l'a, mais
// seulement si sa famille en porte une.
//
// La réduction exceptionnelle, elle, n'est jamais créée ici : sans valeur famille
// à laquelle s'ajouter, elle ne se distingue en rien d'une réduction propre —
// 5 000 propre plus 2 000 de supplément vaut exactement 7 000 propre. Elle
// n'apparaît que si l'élève en porte déjà une, pour l'ajuster ou la retirer.
const getModes = ({ aDejaUnSupplement = false, valeurFamille = 0 } = {}) => [
  // Proposée seulement si la famille en porte une : reprendre une valeur nulle
  // reviendrait à ne rien appliquer sous un nom qui promet autre chose.
  ...(valeurFamille > 0 ? [{
    value: 'famille',
    titre: 'Valeur famille',
    description: `L'élève suivra la valeur de sa famille : ${formatMontant(valeurFamille)} de réduction par mois. Si elle change, son montant suivra.`
  }] : []),
  {
    value: 'personnalisee',
    titre: 'Réduction propre à cet élève',
    description: valeurFamille > 0
      ? "Un montant décidé pour lui seul, qui prend la place de la valeur famille au lieu de s'y ajouter."
      : 'Un montant décidé pour lui seul, appliqué chaque mois.'
  },
  {
    value: 'aucune',
    titre: aDejaUnSupplement ? 'Aucune réduction propre' : 'Aucune réduction',
    description: aDejaUnSupplement
      ? 'Seule la réduction exceptionnelle ci-dessous continuera de s\'appliquer.'
      : "L'élève paie le tarif officiel de sa classe."
  }
];

// Taille commune aux deux boutons du pied de page : un bouton d'annulation plus
// petit que le bouton d'envoi se lit comme une hierarchie qui n'existe pas ici.
const BOUTON_PIED_CLASSES = 'h-9 min-w-[168px] justify-center gap-2';

/**
 * Construit sur les primitives Dialog du projet, et non sur un overlay maison.
 * Radix pose `pointer-events: none` sur le body tant qu'un Dialog est ouvert :
 * un overlay rendu hors de son portail s'affiche normalement mais ne recoit
 * aucun clic. C'est ce qui rendait ce modal inutilisable, ouvert depuis le
 * dossier financier qui est lui-meme un Dialog.
 */
const ModalModifierReductionEleve = ({ eleve, anneeScolaireId, onClose, onSuccess }) => {
  const [situation, setSituation] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [toastInfo, setToastInfo] = useState(null);

  // Toast court ou long selon la longueur du message : `shouldUseLongToast`
  // tranche au-delà de 130 caractères, ou sur demande via `long`/`persistent`.
  const showToast = (message, type = 'success', options = {}) => {
    if (!message) return;
    setToastInfo({ message, type, ...options });
  };
  const hideToast = () => setToastInfo(null);

  const [mode, setMode] = useState('aucune');
  const [reductionMensuel, setReductionMensuel] = useState(0);
  const [reductionMotif, setReductionMotif] = useState('');
  // Modifiables seulement si l'élève porte déjà une réduction exceptionnelle :
  // ce modal n'en crée jamais, il ne fait qu'ajuster ou retirer celle qui existe.
  const [supplementaireMensuel, setSupplementaireMensuel] = useState(0);
  const [supplementaireMotif, setSupplementaireMotif] = useState('');
  const [motifDemande, setMotifDemande] = useState('');

  useEffect(() => {
    let annule = false;

    const charger = async () => {
      setChargement(true);
      setErreur('');
      try {
        const result = await api.eleves.getSituationReduction(eleve.id, anneeScolaireId);
        if (annule) return;

        if (!result.success) {
          setErreur(result.message || "Impossible de lire la situation de cet élève.");
          return;
        }

        const data = result.data;
        setSituation(data);

        // On repart de ce que l'élève porte déjà : ouvrir le formulaire à vide
        // proposerait l'effacement de sa réduction comme état initial.
        const custom = Number(data.reduction_custom_mensuel || 0);
        setMode(data.suit_valeur_famille && Number(data.famille?.reduction_mensuel || 0) > 0
          ? 'famille'
          : (custom > 0 ? 'personnalisee' : 'aucune'));
        setReductionMensuel(custom);
        setReductionMotif(data.reduction_custom_motif || '');
        setSupplementaireMensuel(Number(data.reduction_exceptionnelle_mensuel || 0));
        setSupplementaireMotif(data.reduction_exceptionnelle_motif || '');
      } catch (error) {
        // Le message du serveur porte la cause (route absente, erreur interne) :
        // le remplacer par une phrase generique oblige a aller lire les logs.
        if (!annule) {
          setErreur(error?.message
            ? `Erreur lors du chargement de la situation de l'élève : ${error.message}`
            : "Erreur lors du chargement de la situation de l'élève.");
        }
      } finally {
        if (!annule) setChargement(false);
      }
    };

    charger();
    return () => { annule = true; };
  }, [eleve.id, anneeScolaireId]);

  const fraisMensuel = Number(situation?.frais_mensuel_normal || 0);
  const valeurFamille = Number(situation?.famille?.reduction_mensuel || 0);
  const principale = mode === 'famille'
    ? valeurFamille
    : (mode === 'personnalisee' ? Number(reductionMensuel || 0) : 0);
  const supplementInitial = Number(situation?.reduction_exceptionnelle_mensuel || 0);
  const aDejaUnSupplement = supplementInitial > 0;
  const supplementaire = aDejaUnSupplement ? Number(supplementaireMensuel || 0) : 0;
  // Sous accord, le montant facture est la part negociee, pas le resultat du
  // calcul des reductions. Afficher le second ferait croire a une economie qui
  // n'existe pas : Conrad paie 9 429, pas 11 000.
  const montantApresReductions = Number(situation?.montant_mensuel_actuel || 0);
  const partAccord = situation?.accord_actif
    ? Number(situation.accord_actif.part_eleve || 0)
    : null;
  const avantageAccord = Number(situation?.accord_actif?.avantage_conserve || 0);
  const montantActuel = partAccord !== null ? partAccord : montantApresReductions;

  const apercu = useMemo(() => {
    const demandee = Math.max(0, principale) + Math.max(0, supplementaire);
    const appliquee = Math.min(fraisMensuel, demandee);
    return {
      demandee,
      appliquee,
      plafonnee: demandee > fraisMensuel,
      montant: Math.max(0, fraisMensuel - appliquee)
    };
  }, [fraisMensuel, principale, supplementaire]);

  // Poser une réduction propre plus petite que la valeur famille fait PAYER PLUS :
  // la propre remplace la valeur famille, elle ne s'y ajoute pas. Deux montants
  // absolus, jamais un comparatif — « paiera X de plus » se lisait comme une
  // addition aux frais de la famille.
  const avertissementEviction = useMemo(() => {
    if (mode !== 'personnalisee' || valeurFamille <= 0) return null;
    const propre = Number(reductionMensuel || 0);
    if (propre >= valeurFamille) return null;
    const calculer = (reduction) => Math.max(0, fraisMensuel - Math.min(fraisMensuel, reduction + supplementaire));
    return {
      valeurFamille,
      propre,
      mensuelSiValeurFamille: calculer(valeurFamille),
      mensuelSiPropre: calculer(propre)
    };
  }, [mode, valeurFamille, reductionMensuel, fraisMensuel, supplementaire]);

  // Le même piège dans l'autre sens, et il manquait. L'alerte ci-dessus répond à
  // « auriez-vous mieux fait de garder la valeur famille ? ». Elle ne dit rien du
  // cas où le choix retenu fait payer l'élève PLUS QU'AUJOURD'HUI — reprendre une
  // valeur famille de 1 500 quand il avait 2 000 à lui, ou retirer sa réduction.
  // La comparaison porte donc sur ce qu'il paie et ce qu'il paiera.
  const avertissementHausse = useMemo(() => {
    if (situation === null || montantApresReductions <= 0) return null;
    if (apercu.montant <= montantApresReductions) return null;
    return {
      mensuelActuel: montantApresReductions,
      mensuelApres: apercu.montant,
      raison: mode === 'famille'
        ? 'la valeur famille remplace sa réduction propre'
        : mode === 'aucune'
          ? 'sa réduction propre est retirée'
          : 'la réduction retenue est plus faible que la sienne'
    };
  }, [situation, montantApresReductions, apercu.montant, mode]);

  // Sous accord, l'avantage négocié reste acquis : la réduction demandée s'y
  // ajoute. C'est ce montant que l'élève paiera, pas la base après réductions.
  const partProjetee = partAccord !== null
    ? Math.max(0, apercu.montant - avantageAccord)
    : apercu.montant;

  const riensNeChange = situation !== null
    && (mode === 'famille') === Boolean(situation.suit_valeur_famille)
    && Math.round(mode === 'famille' ? 0 : principale) === Math.round(Number(situation.reduction_custom_mensuel || 0))
    && Math.round(supplementaire) === Math.round(Number(situation.reduction_exceptionnelle_mensuel || 0));

  const envoyer = async () => {
    if (!motifDemande.trim()) {
      showToast('Le motif de la demande est obligatoire.', 'error');
      return;
    }

    if (mode === 'personnalisee' && principale > 0 && !reductionMotif.trim()) {
      showToast("Indiquez le motif de la réduction propre à cet élève.", 'error');
      return;
    }

    if (supplementaire > 0 && !supplementaireMotif.trim()) {
      showToast('Indiquez le motif de la réduction exceptionnelle.', 'error');
      return;
    }

    hideToast();
    setEnvoi(true);
    try {
      const result = await api.eleves.demanderModificationReduction(eleve.id, {
        annee_scolaire_id: situation?.annee_scolaire_id || anneeScolaireId,
        mode_reduction: mode,
        reduction_mensuel: principale,
        reduction_motif: reductionMotif.trim(),
        reduction_exceptionnelle_mensuel: supplementaire,
        reduction_exceptionnelle_motif: supplementaireMotif.trim(),
        // Ramener à zéro une exceptionnelle existante est une demande de retrait,
        // pas une absence de choix.
        retirer_exceptionnelle: aDejaUnSupplement && supplementaire === 0,
        motif: motifDemande.trim()
      });

      if (result.success) {
        onSuccess?.(result);
        return;
      }

      // Les refus du serveur sont explicatifs — situation modifiée entre-temps,
      // demande déjà en attente — donc souvent longs : l'arbitrage court/long
      // les rend lisibles au lieu de les tronquer dans un bandeau étroit.
      showToast(result.message || "La demande n'a pas pu être envoyée.", 'error');
      setEnvoi(false);
    } catch (error) {
      showToast(error.message || "Erreur lors de l'envoi de la demande.", 'error');
      setEnvoi(false);
    }
  };

  const carteMontant = (libelle, valeur, ton = 'neutre', complement = null) => {
    const tons = {
      neutre: 'border-gray-200 bg-gray-50 text-gray-900',
      vert: 'border-emerald-200 bg-emerald-50 text-emerald-800',
      bleu: 'border-[#246990]/20 bg-[#eef7fb] text-[#246990]'
    };
    return (
      <div className={`rounded-md border p-3 ${tons[ton]}`}>
        <div className="text-xs opacity-80">{libelle}</div>
        <div className="font-semibold text-base mt-0.5">{valeur}</div>
        {complement && <div className="mt-1 text-[11px] opacity-90">{complement}</div>}
      </div>
    );
  };

  return (
    <>
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        onPointerDownOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#246990] text-white flex items-center justify-center flex-shrink-0">
                <Calculator className="w-5 h-5" />
              </div>
              <div className="text-left">
                <DialogTitle className="text-lg font-semibold text-gray-900">
                  Modifier la réduction mensuelle
                </DialogTitle>
                <p className="text-sm text-gray-500">
                  {eleve.prenom} {eleve.nom}{eleve.matricule ? ` - ${eleve.matricule}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={envoi}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {chargement ? (
            <div className="py-16 flex items-center justify-center text-[#246990]">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Lecture de la situation…
            </div>
          ) : !situation ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{erreur || 'Situation indisponible.'}</span>
            </div>
          ) : (
            <>
              {/* Situation actuelle : sur toute la largeur, elle sert de référence
                  aux deux colonnes qui suivent. */}
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Situation actuelle</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {carteMontant('Tarif officiel de la classe', formatMontant(fraisMensuel))}
                  {carteMontant(
                    'Réduction appliquée',
                    `- ${formatMontant(situation.reduction_actuelle?.totale)}`,
                    'vert',
                    (Number(situation.reduction_actuelle?.exceptionnelle || 0) > 0
                      && Number(situation.reduction_actuelle?.personnalisee || 0) > 0)
                      ? `${formatMontant(situation.reduction_actuelle.personnalisee)} propre + ${formatMontant(situation.reduction_actuelle.exceptionnelle)} exceptionnelle`
                      : null
                  )}
                  {carteMontant(
                    partAccord !== null ? 'Paie chaque mois (part négociée)' : 'Paie chaque mois',
                    formatMontant(montantActuel),
                    'bleu',
                    partAccord !== null
                      ? `Base après réductions : ${formatMontant(montantApresReductions)}`
                      : null
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Colonne gauche : les choix */}
                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
                    <p className="text-sm font-semibold text-gray-900">Quelle réduction appliquer ?</p>
                    <div className="grid grid-cols-1 gap-2">
                      {getModes({ aDejaUnSupplement, valeurFamille }).map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setMode(option.value)}
                          disabled={envoi}
                          className={`text-left rounded-lg border p-3 transition ${
                            mode === option.value
                              ? 'border-[#246990] bg-[#eef7fb] shadow-sm'
                              : 'border-gray-200 bg-white hover:border-[#246990]/40'
                          }`}
                        >
                          <div className="font-semibold text-gray-900 text-sm">{option.titre}</div>
                          <div className="text-xs text-gray-600 mt-0.5">{option.description}</div>
                        </button>
                      ))}
                    </div>

                    {mode === 'personnalisee' && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-[#246990]/10 bg-[#f8fbfd] p-3">
                        {/* Le champ est un TOTAL retiré du tarif officiel, pas une
                            baisse supplémentaire du montant déjà réduit. Sans cette
                            phrase, ressaisir la réduction en place semble refusé
                            alors qu'elle ne demande simplement aucun changement. */}
                        <p className="sm:col-span-2 text-xs text-gray-600">
                          Montant retiré du tarif officiel de {formatMontant(fraisMensuel)}, et non du
                          montant déjà réduit. Pour descendre plus bas, saisissez le total voulu.
                        </p>
                        <label className="block text-sm">
                          <span className="text-gray-700">Réduction mensuelle</span>
                          <div className="relative mt-1">
                            <input
                              type="number"
                              min="0"
                              max={fraisMensuel}
                              value={reductionMensuel}
                              onChange={(event) => setReductionMensuel(event.target.value)}
                              disabled={envoi}
                              className="w-full h-9 rounded-lg border border-gray-200 px-3 pr-14 focus:border-[#246990] outline-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                          </div>
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-700">
                            Motif {principale > 0 && <span className="text-red-500">*</span>}
                          </span>
                          <input
                            value={reductionMotif}
                            onChange={(event) => setReductionMotif(event.target.value)}
                            placeholder="Ex : accord avec la direction"
                            disabled={envoi}
                            className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 focus:border-[#246990] outline-none"
                          />
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Jamais une option à créer : uniquement une réduction déjà
                      accordée, qu'il faut pouvoir ajuster ou retirer. En proposer
                      une nouvelle ici reviendrait à offrir deux noms pour un seul
                      effet, l'élève n'ayant aucune valeur de famille à évincer. */}
                  {aDejaUnSupplement && (
                    <div className="rounded-lg border border-violet-200 bg-violet-50 p-4">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-violet-900">
                        <PlusCircle className="w-4 h-4" />
                        Réduction exceptionnelle déjà accordée
                      </div>
                      <p className="text-xs text-violet-800 mt-1">
                        Accordée séparément, elle s'ajoute à la réduction ci-dessus. Mettre 0 la retire.
                      </p>

                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="block text-sm">
                          <span className="text-violet-900">Montant mensuel</span>
                          <div className="relative mt-1">
                            <input
                              type="number"
                              min="0"
                              value={supplementaireMensuel}
                              onChange={(event) => setSupplementaireMensuel(event.target.value)}
                              disabled={envoi}
                              className="w-full h-9 rounded-lg border border-violet-200 bg-white px-3 pr-14 focus:border-violet-500 outline-none"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">FCFA</span>
                          </div>
                        </label>
                        <label className="block text-sm">
                          <span className="text-violet-900">
                            Motif {supplementaire > 0 && <span className="text-red-500">*</span>}
                          </span>
                          <input
                            value={supplementaireMotif}
                            onChange={(event) => setSupplementaireMotif(event.target.value)}
                            placeholder="Ex : enfant du personnel"
                            disabled={envoi}
                            className="mt-1 w-full h-9 rounded-lg border border-violet-200 bg-white px-3 focus:border-violet-500 outline-none"
                          />
                        </label>
                      </div>

                      {supplementaire === 0 && (
                        <div className="mt-2 text-[11px] font-medium text-violet-700">
                          Cette réduction de {formatMontant(supplementInitial)} par mois sera retirée.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Colonne droite : ce que la demande produira, et pourquoi */}
                <div className="space-y-4">
                  {avertissementEviction && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                      <p className="text-sm font-semibold text-amber-900">
                        Cette réduction remplace la valeur famille, elle ne s'y ajoute pas
                      </p>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="rounded-md border border-amber-200 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">
                            En gardant la valeur famille ({formatMontant(avertissementEviction.valeurFamille)})
                          </div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissementEviction.mensuelSiValeurFamille)} / mois
                          </div>
                        </div>
                        <div className="rounded-md border border-amber-300 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">
                            Avec cette réduction ({formatMontant(avertissementEviction.propre)})
                          </div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissementEviction.mensuelSiPropre)} / mois
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {avertissementHausse && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                      <p className="text-sm font-semibold text-amber-900">
                        Cette modification fait payer l'élève davantage qu'aujourd'hui
                      </p>
                      <p className="mt-1 text-xs text-amber-800">
                        Parce que {avertissementHausse.raison}.
                      </p>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="rounded-md border border-amber-200 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">Il paie aujourd'hui</div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissementHausse.mensuelActuel)} / mois
                          </div>
                        </div>
                        <div className="rounded-md border border-amber-300 bg-white px-2.5 py-2">
                          <div className="text-[11px] text-gray-500">Après cette modification</div>
                          <div className="font-semibold text-gray-900">
                            {formatMontant(avertissementHausse.mensuelApres)} / mois
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sous accord, le montant facturé vient de la part négociée, pas
                      des réductions : sans recomposition, le geste resterait sans
                      effet. Le dire avant, pas après. */}
                  {situation.accord_actif && (
                    <div className="rounded-lg border border-[#f0c36b] bg-[#fff8e8] p-3 text-xs text-[#7c5b1a]">
                      <div className="font-semibold text-[#9b5c00] mb-1">
                        Cette famille a un accord financier actif (accord #{situation.accord_actif.id})
                      </div>
                      <p>
                        Cet élève y est facturé {formatMontant(situation.accord_actif.part_eleve)} par mois, une part
                        négociée. La validation recomposera l'accord : <span className="font-semibold">sa part sera
                        recalculée</span> sur la nouvelle réduction, et celles des autres membres resteront
                        inchangées.
                      </p>
                      {avantageAccord > 0 && (
                        <p className="mt-2 rounded-md border border-[#f0c36b] bg-white px-2.5 py-2">
                          <span className="font-semibold text-[#9b5c00]">
                            Son avantage négocié de {formatMontant(avantageAccord)} lui reste acquis.
                          </span>{' '}
                          La réduction demandée s'y ajoute : sa part passe de {formatMontant(partAccord)} à{' '}
                          {formatMontant(partProjetee)} par mois. L'effort négocié par l'école ne change pas.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Après validation</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {carteMontant("Aujourd'hui", `${formatMontant(montantActuel)}`)}
                      {carteMontant(
                        'Réduction totale',
                        `- ${formatMontant(apercu.appliquee)}`,
                        'vert',
                        principale > 0 && supplementaire > 0
                          ? `${formatMontant(principale)} propre + ${formatMontant(supplementaire)} exceptionnelle`
                          : null
                      )}
                      {carteMontant(
                        'Paiera',
                        `${formatMontant(partProjetee)}`,
                        'bleu',
                        partAccord !== null
                          ? `Base après réductions : ${formatMontant(apercu.montant)}`
                          : null
                      )}
                    </div>

                    {riensNeChange && (
                      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                        Cette réduction est déjà celle appliquée aujourd'hui, il n'y a donc rien à demander.
                        Pour aller plus loin, saisissez le total voulu : {formatMontant(Math.min(fraisMensuel, principale + 2000))}
                        {' '}ferait passer l'élève à {formatMontant(Math.max(0, fraisMensuel - Math.min(fraisMensuel, principale + supplementaire + 2000)))} par mois.
                      </div>
                    )}

                    {partAccord !== null && avantageAccord > 0 && principale + supplementaire > 0 && (
                      <div className="mt-3 rounded-md border border-[#246990]/20 bg-[#eef7fb] px-3 py-2 text-xs text-[#174866]">
                        Payait {formatMontant(partAccord)} · paiera <span className="font-semibold">{formatMontant(partProjetee)}</span>.
                        <div className="mt-1 text-gray-600">
                          Baisse totale de {formatMontant(Math.max(0, fraisMensuel - partProjetee))} sur son tarif de{' '}
                          {formatMontant(fraisMensuel)} : {formatMontant(apercu.appliquee)} de réduction propre à
                          l'élève et {formatMontant(avantageAccord)} d'avantage négocié par la famille.
                        </div>
                      </div>
                    )}

                    {apercu.plafonnee && (
                      <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Le total demandé ({formatMontant(apercu.demandee)}) dépasse le tarif de la classe :
                        la réduction sera ramenée à {formatMontant(fraisMensuel)} et l'élève paiera 0.
                      </div>
                    )}

                    <div className="mt-3 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-900">
                      <Calendar className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <div>
                        {situation.mois_effet?.nom ? (
                          <>
                            Effet à partir de <span className="font-semibold">{situation.mois_effet.nom}</span>.
                            Les mois déjà réglés ou entamés gardent leur montant.
                          </>
                        ) : (
                          <>
                            Tous les mois de cet exercice ont déjà commencé à être payés : aucun mois ne sera
                            recalculé si la demande est validée.
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  <label className="block rounded-lg border border-gray-200 bg-white p-4">
                    <span className="text-sm font-semibold text-gray-900">
                      Motif de la demande <span className="text-red-500">*</span>
                    </span>
                    <textarea
                      value={motifDemande}
                      onChange={(event) => setMotifDemande(event.target.value)}
                      placeholder="Ex : accord conclu avec le parent le 12/08, dossier social complété…"
                      rows={3}
                      disabled={envoi}
                      className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15 outline-none resize-none"
                    />
                  </label>

                  <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-[#246990] mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-[#174866]">
                      <div className="font-semibold">Validation obligatoire</div>
                      <p className="mt-1">
                        Rien n'est appliqué maintenant : la demande part au Centre de Support et sera
                        exécutée après validation.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="px-5 py-4 border-t border-gray-200 bg-gray-50 sm:justify-between">
          <Button variant="outline" onClick={onClose} disabled={envoi} className={BOUTON_PIED_CLASSES}>
            <X className="w-4 h-4" />
            Annuler
          </Button>
          <Button
            onClick={envoyer}
            disabled={envoi || chargement || !situation || riensNeChange}
            title={riensNeChange ? 'Aucun changement par rapport à la situation actuelle' : undefined}
            className={`${BOUTON_PIED_CLASSES} bg-[#246990] hover:bg-[#1f5a7a] text-white`}
          >
            {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {envoi ? 'Envoi en cours…' : 'Envoyer la demande'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Hors du Dialog : Radix rend son contenu dans son propre portail, et un
        toast laissé à l'intérieur resterait prisonnier de sa pile. */}
    {toastInfo && (
      <ToastPortal>
        {shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
          <LongToast
            show
            title={toastInfo.title}
            message={toastInfo.message}
            type={toastInfo.type}
            onClose={hideToast}
          />
        ) : (
          <Toast message={toastInfo.message} type={toastInfo.type} onClose={hideToast} />
        )}
      </ToastPortal>
    )}
    </>
  );
};

export default ModalModifierReductionEleve;
